import React, {useEffect, useState} from 'react';
import {createPortal} from 'react-dom';

export interface ApplicationJourneySubStepItem {
  key: string;
  title: string;
}

interface ApplicationJourneySubStepProgressProps {
  steps: ApplicationJourneySubStepItem[];
  activeIndex: number;
  ariaLabel?: string;
  onStepClick?: (index: number) => void;
}

const ApplicationJourneySubStepProgress: React.FC<
  ApplicationJourneySubStepProgressProps
> = ({steps, activeIndex, ariaLabel = 'Form progress', onStepClick}) => {
  const [portalTarget, setPortalTarget] = useState<Element | null>(null);

  useEffect(() => {
    if (typeof document === 'undefined') return;
    const nextTarget = document.querySelector('[data-money-substep-nav]');
    setPortalTarget((current) =>
      current === nextTarget ? current : nextTarget,
    );
  }, [activeIndex, steps.length]);

  const total = steps.length;
  const progress = (
    <ol className="substep-progress" aria-label={ariaLabel}>
      {steps.map((step, index) => {
        const isComplete = index < activeIndex;
        const isCurrent = index === activeIndex;
        const isClickable = typeof onStepClick === 'function';

        return (
          <li
            key={step.key}
            className={`substep-progress__item${
              isComplete ? ' is-complete' : ''
            }${isCurrent ? ' is-current' : ''}${
              isClickable ? ' is-clickable' : ''
            }`}
          >
            {isClickable ? (
              <button
                type="button"
                className="substep-progress__button"
                onClick={() => onStepClick(index)}
                aria-current={isCurrent ? 'step' : undefined}
              >
                <span
                  className="substep-progress__indicator"
                  aria-hidden="true"
                >
                  <span className="substep-progress__index">{index + 1}</span>
                  <span className="substep-progress__track" />
                </span>
                <span className="substep-progress__label">{step.title}</span>
                <span className="sr-only">
                  {isCurrent
                    ? `Step ${index + 1} of ${total}, ${step.title}, current`
                    : `Step ${index + 1} of ${total}, ${step.title}`}
                </span>
              </button>
            ) : (
              <>
                <span
                  className="substep-progress__indicator"
                  aria-hidden="true"
                >
                  <span className="substep-progress__index">{index + 1}</span>
                  <span className="substep-progress__track" />
                </span>
                <span className="substep-progress__label">{step.title}</span>
                <span className="sr-only">
                  {isCurrent
                    ? `Step ${index + 1} of ${total}, ${step.title}, current`
                    : `Step ${index + 1} of ${total}, ${step.title}`}
                </span>
              </>
            )}
          </li>
        );
      })}
    </ol>
  );

  if (portalTarget && (portalTarget as Node).isConnected) {
    return createPortal(progress, portalTarget);
  }

  return progress;
};

export default ApplicationJourneySubStepProgress;
